import { Box, Button, Chip, Rating, Stack, StackProps, Tooltip, Typography } from "@mui/material"
import { PlusIcon, EditIcon } from "shared/icons"
import { ArticleResult } from "interface/article"
import { EditApproachDialog } from "features/kojin/dialog/editApproach"
import { useApproach } from "./container"
import { useUserSession } from "features/session/context"

type ApproachListBlockProps = {
  articles: ArticleResult[] | null
  onArticleOpen: (article: ArticleResult, list: ArticleResult[]) => void
} & ReturnType<typeof useApproach> & StackProps
export const ApproachListBlock = ({
	searchId,
	authorId,
  approachList,
  setApproachList,
  editApproach,
  setEditApproach,
  articles,
  onArticleOpen
}: ApproachListBlockProps) => {
  const { isCareNet, isGeneral } = useUserSession()
  if (!searchId || !authorId) return <></>
  return (
    <>
      <Stack direction="column">
        {approachList.length > 0 ? (
          <Stack direction='column'>
            {approachList.map((approach, i) => (
              <Stack key={`approach-${approach.approachId}-${i}`} direction='row' padding={1} spacing={1} alignItems='flex-start'>
                <Stack flexGrow={1} direction='column' spacing={0.5}>
                  <Stack direction='row' spacing={1} paddingX={1} borderBottom={1} borderColor="divider">
                    <Typography variant="body2" color='text.primary' component='div' flexGrow={1}>{approach.title}</Typography>
                    { approach.ownFlag && <Chip label='自社製品' size='small' color='success' /> }
                    { approach.othersFlag && <Chip label='競合製品' size='small' color='warning' /> }
                    { approach.priorityContentFlag && <Chip label='承認外' size='small' color='error' /> }
                    { approach.articleScore && (
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <Rating readOnly value={(approach.articleScore * 5) / 17} max={5} precision={0.1} size='small'/>
                        <Typography variant='caption' color='text.secondary'>{approach.articleScore}/17 点</Typography>
                      </Stack>
                    ) }
                  </Stack>
                  <Stack direction='column' spacing={0.5} paddingX={1}>
                    { approach.reason && <Typography variant="body2" color='text.secondary' whiteSpace='pre-wrap'>{approach.reason}</Typography> }
                    { approach.ref && approach.ref.length > 0 && (
                      <Stack direction='row' flexWrap="wrap" useFlexGap>
                        <Typography variant="caption" color='text.secondary' component='div' marginRight={1}>参照論文</Typography>
                        { approach.ref.map((ref, k) => {
                          if (!articles) return <></>
                          const index = articles.findIndex(article => article.articleId === ref.articleId) ?? -1
                          if (index < 0) return <></>
                          const article = articles[index]
                          if (!article) return <></>
                          return (
                            <Tooltip key={`approach-ref-${i}-${k}`} title={ref.linkText} placement='top'>
                              <Chip label={`${index+1}`} variant='outlined' size='small' sx={{ marginRight: 0.5, borderRadius: 1, height: 'inherit' }} onClick={() => {
                                const list = articles?.filter((journal) => approach.ref.some((r) => r.articleId === journal.articleId)) ?? []
                                onArticleOpen(article, list)
                              }}/>
                            </Tooltip>
                          )
                        }) }
                      </Stack>
                    ) }
                  </Stack>
                </Stack>
                {isCareNet && isGeneral && (
                  <Button
                    variant="text"
                    size='small'
                    startIcon={<EditIcon/>}
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => setEditApproach({ action: 'edit', data: approach })}
                  >
                    編集
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        ) : <></>}

        {isCareNet && isGeneral && (
          <Box paddingX={1}>
            <Button
              variant="text"
              size='small'
              startIcon={<PlusIcon/>}
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => setEditApproach({ action: 'create', data: null })}
            >
              項目を追加
            </Button>
          </Box>
        )}
      </Stack>

      {(editApproach && isCareNet && isGeneral) && (
        <EditApproachDialog
          searchId={searchId}
          authorId={authorId}
          data={editApproach.data}
          articles={articles ?? []}
          onDelete={() => {
            if (!editApproach || editApproach.action !== 'edit' || !editApproach.data) return
            const removeId = editApproach.data.approachId
            setApproachList(approachList.filter((approach) => approach.approachId !== removeId))
            setEditApproach(null)
          }}
          onSave={(data) => {
            if (!editApproach) return
            if (editApproach.action === 'edit') {
              if (!editApproach.data) return
              const editId = editApproach.data.approachId
              setApproachList(approachList.map((approach) => approach.approachId === editId ? data : approach))
            } else if (editApproach.action === 'create') {
              setApproachList(approachList.concat([data]))
            }
            setEditApproach(null)
          }}
          onClose={() => setEditApproach(null)}
        />
      )}
    </>
  )
}